
import Vue from "vue"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import {MemberSnapshotMeasure, MemberSnapshotDimension} from "@/cube"
import {Prop} from "vue-property-decorator"
import Component from "vue-class-component"
import {MetricSliced, MetricStore} from "@/cube/metric-data/MetricStore"
import * as _ from "lodash"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {toKey} from "@/cube/toKey"
import {theme} from "@/theme"
import NoDataMessage from "@/ui/NoDataMessage.vue"

const attentionColor = am4core.color("#bf2b3d")
const t = theme()
const bulletAdapter = (fill: am4core.Color, target: am4charts.CircleBullet) =>
  (<am4charts.LineSeriesDataItem>target.dataItem).valueY < 0
    ? am4core.color(attentionColor)
    : am4core.color(t.positiveColor)

@Component({
  components: {NoDataMessage},
})
export default class AccountBalanceChart extends Vue {
  @Prop()
  metricStore: MetricStore

  private hasData = true
  private ready = false
  private chart: am4charts.XYChart

  async mounted() {
    const metricData = this.metricStore.getSliced(
      MemberSnapshotMeasure.countMembers,
      [MemberSnapshotDimension.ageGroup, MemberSnapshotDimension.balanceCompare]
    )

    this.chart = this.createChart()

    this.$watch(
      () => metricData.end,
      () => {
        if (metricData.end) {
          this.hasData = metricData.end.length > 0
          if (this.hasData) {
            this.chart.show()
            this.updateChartData(metricData)
            this.ready = true
          } else {
            this.chart.hide()
          }
        }
      },
      {immediate: true}
    )
  }

  updateChartData(
    metricSliced: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    console.log("Updating data", metricSliced)

    metricSliced.end!.forEach(row => {
      row[MemberSnapshotDimension.ageGroup] = removeDimensionSortPrefix(
        row[MemberSnapshotDimension.ageGroup]
      )
    })

    if (_.isEmpty(this.chart.data)) {
      this.chart.data = <any>metricSliced.end
    } else {
      keyedDataUpdate(
        MemberSnapshotMeasure.countMembers,
        (row: any) =>
          toKey(row, [
            MemberSnapshotDimension.ageGroup,
            MemberSnapshotDimension.balanceCompare,
          ]),
        metricSliced.end!,
        this.chart.data
      )
      this.chart.series.values[0].validateDataItems()
      this.chart.invalidateRawData()
    }
  }

  createChart() {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.XYChart)

    // Create axes
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
    categoryAxis.title.text = "Age Group"
    categoryAxis.dataFields.category = MemberSnapshotDimension.ageGroup
    categoryAxis.renderer.minGridDistance = 65
    categoryAxis.cursorTooltipEnabled = false

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    valueAxis.title.text = "Index"
    valueAxis.cursorTooltipEnabled = false

    const range = valueAxis.axisRanges.create()
    range.value = 0
    range.endValue = -0.05
    range.axisFill.fillOpacity = 0.4
    range.grid.strokeOpacity = 0

    chart.series.push(createSeries())

    return chart
  }

  beforeDestroy() {
    this.chart.dispose()
  }
}

function createSeries() {
  const lineSeries = new am4charts.LineSeries()
  lineSeries.dataFields.valueY = MemberSnapshotDimension.balanceCompare
  lineSeries.dataFields.categoryX = MemberSnapshotDimension.ageGroup
  lineSeries.dataFields.value = MemberSnapshotMeasure.countMembers
  lineSeries.strokeOpacity = 0

  const bullet = lineSeries.bullets.push(new am4charts.CircleBullet())
  bullet.nonScalingStroke = true
  bullet.tooltipText =
    "{valueY.formatNumber('#%')} of benchmark \nMembers: {value}"

  bullet.adapter.add("tooltipText", (text, target) =>
    (<am4charts.LineSeriesDataItem>target.dataItem).valueY < 0
      ? text?.replace("of", "below")
      : text?.replace("of", "above")
  )

  bullet.adapter.add("fill", bulletAdapter)
  bullet.adapter.add("stroke", bulletAdapter)

  bullet.adapter.add("fillOpacity", (opacity, target) => {
    console.log(target)
    return (<am4charts.LineSeriesDataItem>target.dataItem).value === 0
      ? 0
      : opacity
  })

  bullet.fillOpacity = 0.55
  bullet.strokeOpacity = 0.75
  bullet.strokeWidth = 1

  lineSeries.heatRules.push({
    target: bullet.circle,
    min: 3,
    max: 12,
    property: "radius",
  })

  return lineSeries
}
